import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudUpload, Delete, UploadFile } from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';

import { State } from '../../redux/mainReducer';
import { fileToBase64 } from '../../Document/DocumentOverview';
import {
  ILegalClientDocumentFile,
  ILegalClientDocumentTypeEntry,
  formatFileSize,
  getCurrentVersion,
} from '../../Interfaces/ILegalClientDocumentVersion';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Gesetzt, wenn eine neue Version eines bestehenden Dokuments entsteht. */
  typeEntry: ILegalClientDocumentTypeEntry | null;
  /**
   * Auswählbare Dokumenttypen — nur gefüllt, wenn ein *neues* Dokument angelegt
   * wird (dann ist `typeEntry` null).
   */
  selectableTypeArray?: ILegalClientDocumentTypeEntry[];
  onSave: (
    idLegalClientDocumentType: number,
    fileArray: ILegalClientDocumentFile[],
    validUntil: string,
    note: string
  ) => void;
}

/**
 * Neue Version eines Dokumenttyps zusammenstellen. Eine Version kann aus
 * mehreren Dateien bestehen — etwa Vorder- und Rückseite eines Ausweises. Sie
 * wird erst mit dem Speichern des Mandanten übernommen.
 */
export const DocumentVersionUploadDialog: React.FC<IProps> = props => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const [fileArray, setFileArray] = useState<ILegalClientDocumentFile[]>([]);
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [warning, setWarning] = useState('');
  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (props.isOpen) {
      setFileArray([]);
      setValidUntil('');
      setNote('');
      setWarning('');
      setIsDragOver(false);
      setSelectedTypeId(
        props.typeEntry?.idLegalClientDocumentType ??
          props.selectableTypeArray?.[0]?.idLegalClientDocumentType ??
          ''
      );
    }
  }, [props.isOpen, props.typeEntry, props.selectableTypeArray]);

  const currentVersion =
    props.typeEntry === null ? undefined : getCurrentVersion(props.typeEntry);
  const nextVersionNumber = (currentVersion?.VersionNumber ?? 0) + 1;

  const handleAddFiles = async (selectedFileArray: File[]) => {
    const rejectedArray: string[] = [];
    const additionArray: ILegalClientDocumentFile[] = [];
    let nextId = -1 - fileArray.length;

    for (const file of selectedFileArray) {
      const mimeType = mimeTypeArray.find(
        entry => entry.MIMEType === file.type
      );

      if (mimeType === undefined) {
        rejectedArray.push(file.name);
        continue;
      }

      additionArray.push({
        idDocument: nextId,
        FileName: file.name,
        idMIMEType: mimeType.idMIMEType,
        FileSize: file.size,
        Label: '',
        Position: fileArray.length + additionArray.length,
        Data: await fileToBase64(file),
      });

      nextId = nextId - 1;
    }

    setFileArray([...fileArray, ...additionArray]);
    setWarning(
      rejectedArray.length === 0
        ? ''
        : `Nicht unterstützte Dateiart: ${rejectedArray.join(', ')}`
    );
  };

  const handleChangeLabel = (idDocument: number, label: string) => {
    setFileArray(
      fileArray.map(entry =>
        entry.idDocument === idDocument ? { ...entry, Label: label } : entry
      )
    );
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        {props.typeEntry === null
          ? 'Dokument hochladen'
          : 'Neue Version hochladen'}
        {props.typeEntry !== null && (
          <Typography variant='body2' color='text.secondary'>
            {props.typeEntry.LegalClientDocumentType} — wird Version{' '}
            {nextVersionNumber}
            {currentVersion !== undefined &&
              `; Version ${currentVersion.VersionNumber} bleibt als Historie erhalten`}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity='info' sx={{ mb: 2 }}>
          Eine Version kann aus mehreren Dateien bestehen — etwa Vorder- und
          Rückseite eines Ausweises. Alle hier hinzugefügten Dateien gehören zu
          einer gemeinsamen Version. Übernommen wird das erst mit dem Speichern
          des Mandanten.
        </Alert>

        {warning !== '' && (
          <Alert
            severity='warning'
            sx={{ mb: 2 }}
            onClose={() => setWarning('')}
          >
            {warning}
          </Alert>
        )}

        {props.typeEntry === null && (
          <TextField
            label='Dokumenttyp'
            value={selectedTypeId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSelectedTypeId(Number(event.target.value))
            }
            size='small'
            select
            fullWidth
            sx={{ mb: 2 }}
            helperText={
              (props.selectableTypeArray ?? []).length === 0
                ? 'Für diese Mandantenart sind keine Dokumenttypen hinterlegt.'
                : 'Vorgesehen für die Mandantenart dieses Mandanten'
            }
          >
            {(props.selectableTypeArray ?? []).map(entry => (
              <MenuItem
                key={`type-${entry.idLegalClientDocumentType}`}
                value={entry.idLegalClientDocumentType}
              >
                {entry.LegalClientDocumentType}
                {entry.VersionArray.length > 0 &&
                  ` (bereits hinterlegt — wird Version ${entry.VersionArray.length + 1})`}
              </MenuItem>
            ))}
          </TextField>
        )}

        <input
          type='file'
          multiple
          hidden
          ref={uploadInputRef}
          accept={mimeTypeArray.map(mimeType => mimeType.MIMEType).join(',')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files !== null) {
              handleAddFiles(Array.from(event.target.files));
            }
            event.target.value = '';
          }}
        />

        <Button
          variant='outlined'
          startIcon={<UploadFile />}
          onClick={() => uploadInputRef.current?.click()}
        >
          Dateien auswählen
        </Button>

        {fileArray.length === 0 ? (
          <Stack
            alignItems='center'
            spacing={1}
            onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(event: React.DragEvent<HTMLDivElement>) => {
              event.preventDefault();
              setIsDragOver(false);

              if (event.dataTransfer.files.length > 0) {
                handleAddFiles(Array.from(event.dataTransfer.files));
              }
            }}
            onClick={() => uploadInputRef.current?.click()}
            sx={{
              mt: 2,
              py: 4,
              border: '1px dashed',
              borderColor: isDragOver ? 'primary.main' : 'divider',
              backgroundColor: isDragOver ? 'action.hover' : 'transparent',
              borderRadius: 1,
              color: isDragOver ? 'primary.main' : 'text.secondary',
              cursor: 'pointer',
            }}
          >
            <CloudUpload color='inherit' />
            <Typography variant='body2' color='inherit'>
              {isDragOver
                ? 'Dateien hier ablegen'
                : 'Dateien hierher ziehen oder klicken, um sie auszuwählen.'}
            </Typography>
          </Stack>
        ) : (
          <Table size='small' sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Datei</TableCell>
                <TableCell width={120}>Größe</TableCell>
                <TableCell width={260}>
                  Bezeichnung (z. B. Vorderseite)
                </TableCell>
                <TableCell width={70} align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {fileArray.map(entry => (
                <TableRow key={`upload-${entry.idDocument}`}>
                  <TableCell>{entry.FileName}</TableCell>
                  <TableCell>{formatFileSize(entry.FileSize)}</TableCell>
                  <TableCell>
                    <TextField
                      value={entry.Label ?? ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeLabel(entry.idDocument, event.target.value)
                      }
                      size='small'
                      fullWidth
                      placeholder='optional'
                      inputProps={{ maxLength: 150 }}
                    />
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip title='Datei entfernen'>
                      <IconButton
                        size='small'
                        onClick={() =>
                          setFileArray(
                            fileArray.filter(
                              file => file.idDocument !== entry.idDocument
                            )
                          )
                        }
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 3 }}
        >
          <TextField
            label='Gültig bis'
            type='date'
            value={validUntil}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setValidUntil(event.target.value)
            }
            size='small'
            InputLabelProps={{ shrink: true }}
            helperText='optional, z. B. Ablaufdatum des Ausweises'
          />
          <TextField
            label='Anmerkung'
            value={note}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNote(event.target.value)
            }
            size='small'
            fullWidth
            inputProps={{ maxLength: 255 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={() => props.setIsOpen(false)}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          disabled={fileArray.length === 0 || selectedTypeId === ''}
          onClick={() => {
            props.onSave(selectedTypeId as number, fileArray, validUntil, note);
            props.setIsOpen(false);
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
